import React, { useMemo, useState } from 'react'

import Header from '_components/header'
import User from '_lib/User.js'

import StageStepper from '../bulk-edit-audiences/components/stage-stepper'
import { DEFAULT_ENV } from '../predactiv-admin/helpers'
import SelectStage from './stages/select-stage'
import ReviewStage from './stages/review-stage'
import MoveStage from './stages/move-stage'
import { getSkipReason } from './lib/move'

import '../bulk-edit-audiences/styles.scss'
import './styles.scss'

const STAGES = [
  { id: 'select', label: 'Select' },
  { id: 'review', label: 'Review' },
  { id: 'move', label: 'Move' },
]

const MoveAudiencesToPdpPage = () => {
  const [stage, setStage] = useState('select')
  const [audiences, setAudiences] = useState([])
  // audience ids the user removed from the move on the review stage
  const [excludedIds, setExcludedIds] = useState(() => new Set())
  const [env, setEnv] = useState(DEFAULT_ENV)
  const [organization, setOrganization] = useState(null)
  // bumped on every move so the move stage starts from a clean slate
  const [runId, setRunId] = useState(0)

  // same permission datasphere-api checks on the move endpoint
  const canMove = Boolean(
    User?.data?.datasphere_permissions?.predactiv?.permissions?.organization?.update
  )

  // audiences that can't be moved (already in PDP, deleted, ...) — excluded for good
  const skipReasons = useMemo(
    () =>
      Object.fromEntries(
        audiences
          .map((audience) => [audience._id, getSkipReason(audience)])
          .filter(([, reason]) => reason)
      ),
    [audiences]
  )

  const audiencesToMove = useMemo(
    () => audiences.filter(({ _id }) => !skipReasons[_id] && !excludedIds.has(_id)),
    [audiences, skipReasons, excludedIds]
  )

  const toggleExcluded = (id) =>
    setExcludedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const handleLoaded = (loaded) => {
    setAudiences(loaded)
    setExcludedIds(new Set())
    setStage('review')
  }

  const handleStart = () => {
    setRunId((id) => id + 1)
    setStage('move')
  }

  const handleNewMove = () => {
    setAudiences([])
    setExcludedIds(new Set())
    setOrganization(null)
    setStage('select')
  }

  return (
    <div id="move-audiences-to-pdp-page">
      <Header title="Move Audiences to PDP" className="header__container" />

      <div className="bulk-edit">
        <Choose>
          <When condition={!canMove}>
            <div className="bulk-edit__notice">
              You are not authorized to move audiences to PDP. It requires the Predactiv
              organization update permission.
            </div>
          </When>
          <Otherwise>
            <div className="bulk-edit__notice">
              This tool moves audience creator audiences into a PDP organization: each one is linked
              to a new PDP conversation, keeping its name and external ids. The move runs from your
              browser in small batches: keep this tab open while it is in progress.
            </div>

            <StageStepper stages={STAGES} current={stage} />

            <Choose>
              <When condition={stage === 'select'}>
                <SelectStage onLoaded={handleLoaded} />
              </When>
              <When condition={stage === 'review'}>
                <ReviewStage
                  audiences={audiences}
                  skipReasons={skipReasons}
                  excludedIds={excludedIds}
                  onToggleExclude={toggleExcluded}
                  onClearExcluded={() => setExcludedIds(new Set())}
                  env={env}
                  onEnvChange={setEnv}
                  organization={organization}
                  onOrganizationChange={setOrganization}
                  onBack={() => setStage('select')}
                  onStart={handleStart}
                />
              </When>
              <When condition={stage === 'move'}>
                <MoveStage
                  key={`move-${runId}`}
                  audiences={audiencesToMove}
                  organization={organization}
                  env={env}
                  onNewMove={handleNewMove}
                />
              </When>
            </Choose>
          </Otherwise>
        </Choose>
      </div>
    </div>
  )
}

export default MoveAudiencesToPdpPage
